// Demanda esperada y reorden sugerido de UN producto · I5
//
// Se monta en la ficha del producto (pestaña «Demanda», la monta el
// integrador) y en la pantalla del carril. Todo lo que pinta viene
// precalculado: dos lecturas y nada más. Con SIN_DATOS no hay cifra, y
// se dice por qué. Lo entrenado con el gemelo lleva su etiqueta.
import { useEffect, useState } from 'react';
import { TrendingUp, PackageSearch, FlaskConical } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import { numero, fecha, tonoConfianza, CLASE_TONO, textoConfianza } from './util';

export function ChipSintetico() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-800">
      <FlaskConical className="h-3 w-3" /> Entrenado con datos sintéticos
    </span>
  );
}

// La ayuda va escrita con su id literal en cada tarjeta (no por prop):
// la prueba que comprueba que toda nota del catálogo se pinta busca
// la etiqueta con su id tal cual, y con razón: una nota que solo existe como
// variable no se puede auditar.
function Tarjeta({ icono: Icono, titulo, ayuda, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <Icono className="h-4 w-4 text-gray-500" />
        <h3 className="font-semibold text-gray-900">{titulo}</h3>
      </div>
      {ayuda}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function SinDatos({ mensaje }) {
  return (
    <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
      <span className="font-semibold text-gray-700">Sin datos.</span> {mensaje}
    </p>
  );
}

export default function DemandaYReorden({ idProducto }) {
  const [pred, setPred] = useState(null);
  const [reorden, setReorden] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!idProducto) return undefined;
    let vigente = true;
    Promise.all([
      api.get(`/inteligencia/prediccion?id_producto=${idProducto}`),
      api.get(`/inteligencia/reorden?id_producto=${idProducto}`),
    ])
      .then(([p, r]) => { if (vigente) { setPred(p); setReorden(r); setError(null); } })
      .catch((e) => vigente && setError(e.message || 'No se pudo consultar la inteligencia de inventario.'));
    return () => { vigente = false; };
  }, [idProducto]);

  if (error) return <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{error}</p>;
  if (!pred || !reorden) return <p className="text-sm text-gray-500">Calculando… (leyendo lo precalculado esta madrugada)</p>;

  const horizontes = pred.horizontes || [];
  const sintetico = horizontes.some((h) => h.sintetico) || reorden.sintetico;

  return (
    <div className="space-y-3">
      {sintetico && (
        <div className="flex flex-wrap items-center gap-2">
          <ChipSintetico />
          <Ayuda id="inteligencia.sintetico" />
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <Tarjeta icono={TrendingUp} titulo="Demanda esperada" ayuda={<Ayuda id="inteligencia.demanda" />}>
          {pred.estado !== 'OK' ? <SinDatos mensaje={pred.mensaje} /> : (
            <div className="grid grid-cols-2 gap-3">
              {horizontes.map((h) => (
                <div key={h.horizonte_dias} className={`rounded-lg border p-3 ${CLASE_TONO[h.estado === 'OK' ? tonoConfianza(h.confianza) : 'gris']}`}>
                  <p className="text-[11px] font-semibold uppercase">{h.horizonte_dias} días</p>
                  {h.estado === 'OK' ? (
                    <>
                      <p className="text-2xl font-semibold tabular-nums">{numero(h.demanda_esperada)}</p>
                      <p className="text-xs">entre {numero(h.intervalo_bajo)} y {numero(h.intervalo_alto)} (8 de cada 10 veces)</p>
                      <p className="mt-1 text-xs font-semibold">{textoConfianza(h.confianza)}</p>
                      <p className="text-[11px]">Dato al {fecha(h.dato_al)} · {h.dias_observados} días observados</p>
                    </>
                  ) : <p className="text-sm">Sin datos</p>}
                </div>
              ))}
            </div>
          )}
        </Tarjeta>
        <Tarjeta icono={PackageSearch} titulo="Reorden sugerido" ayuda={<Ayuda id="inteligencia.reorden" />}>
          {reorden.estado !== 'OK' ? <SinDatos mensaje={reorden.mensaje} /> : (
            <div className={`rounded-lg border p-3 ${CLASE_TONO[reorden.bajo_reorden ? 'amarillo' : tonoConfianza(reorden.confianza)]}`}>
              <p className="text-2xl font-semibold tabular-nums">{numero(reorden.punto_reorden)}</p>
              <p className="text-xs">
                = {numero(reorden.demanda_plazo)} que se espera vender en {reorden.plazo_dias} días + {numero(reorden.stock_seguridad)} de seguridad
              </p>
              <p className="mt-1 text-sm font-semibold">
                {reorden.bajo_reorden
                  ? `Existencia ${numero(reorden.existencia)}: ya está por debajo. Conviene pedir.`
                  : `Existencia ${numero(reorden.existencia)}: todavía alcanza.`}
              </p>
              <p className="text-[11px]">{textoConfianza(reorden.confianza)} · Dato al {fecha(reorden.dato_al)}</p>
              {reorden.punto_reorden_fijo > 0 && (
                <p className="mt-1 text-[11px]">El punto de reorden fijo del producto es {numero(reorden.punto_reorden_fijo)}; éste se recalcula cada madrugada.</p>
              )}
              <p className="mt-1 text-[11px]">TEGO no compra solo: esto es una sugerencia.</p>
            </div>
          )}
        </Tarjeta>
      </div>
    </div>
  );
}
